import React, { useContext } from "react";
import "../NewTicket.scss";
import { Col, Row, Layout, Card } from "antd";
import { formatDate } from "../../../helpers/dateHelper";
import {
  computeHotelPgTransactionFee,
  getHotelPaymentStatusText,
  shouldShowHotelPaymentStatus,
  hasHotelConfirmationNumber,
  getHotelConfirmationNumberForDisplay,
  shouldShowHotelLastCancellationDate,
  shouldShowHotelLastVoucherDate,
  formatHotelTicketCancellationDateForDisplay,
  getHotelPrice,
  getHotelTicketPayableBase,
  getHotelTicketCommissionDiscount,
  getHotelTicketDisplayCommission,
  getHotelTicketDisplayTds,
  getHotelTicketDisplayTotal,
  getHotelAgentTicketFareDisplay,
  isHotelAgentB2BTicket,
  getEffectiveHotelBookingStatus,
  getHotelCancellationReasonLines,
  getStatus,
  shouldCollapseHotelSamePanRoomForms,
  isHotelAdultGuest,
  guestHasPan,
  guestHasPassport,
  shouldShowPanOnHotelTicket,
  shouldShowPassportOnHotelTicket,
  formatHotelGuestDisplayName,
  groupHotelTicketGuestsByRoom,
  getHotelTicketGuestCount,
  getHotelTicketRoomCount,
  getHotelSpecialRequestFromTicket,
  formatRoomComboTitle,
} from "./hotelhelper";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import parse from "html-react-parser";
import { useCurrencyContext } from "../../providers/CurrencyProvider";
import { GlobalStatesContext } from "../../providers";
import { useAuthContext } from "../../providers/AuthProvider";

import HotelTicketVoucherPanSection from "./HotelTicketVoucherPanSection";
import { getRoomMealCategoryLabel } from "../../../pages/Hotels/hotelMealTypes";
import { getHotelCurrencySymbol } from "../../../pages/Hotels/hotelCurrency";
import HotelSpecialRequest from "../../../pages/Hotels/HotelSpecialRequest/HotelSpecialRequest";

let dateFormat = "MMM, DD-YYYY";

const TicketHotel = ({
  ticketData,
  cmsFareRules,
  showVoucherMultiRoomPan = false,
  samePanForAllRooms = false,
  verifiedPanRoomIndex = null,
  onSamePanForAllRoomsChange = () => {},
  onTicketPanVerified = () => {},
  panFieldErrors = {},
  onPanFieldTouched = () => {},
}) => {
  const {
    activeCurrency: selectedCurrency,
    currencyValue,
  } = useCurrencyContext();
  const activeCurrency = getHotelCurrencySymbol(selectedCurrency);
  const {
    state: {
      otherData: { transactionFee },
      SeletedPayOption,
    },
  } = useContext(GlobalStatesContext);
  const {
    isLogin: { agent },
  } = useAuthContext();
  const fareTotals = getHotelPrice(ticketData);
  let {
    baseAmount,
    taxAmount,
    convienenceFee,
    discount,
    grandTotal,
    totalPrice,
    insuranceTotal,
    noOfNights,
    otherCharges,
  } = fareTotals;
  const payableBase = getHotelTicketPayableBase(ticketData, fareTotals);
  const pgTransactionFee = computeHotelPgTransactionFee(
    transactionFee,
    payableBase,
    SeletedPayOption,
  );
  const commissionDiscount = getHotelTicketDisplayCommission(ticketData);
  const isAgentB2B = isHotelAgentB2BTicket(ticketData, agent);
  const agentFareDisplay = isAgentB2B
    ? getHotelAgentTicketFareDisplay(ticketData)
    : null;
  const displayTds = isAgentB2B
    ? Number(agentFareDisplay?.tds ?? 0)
    : getHotelTicketDisplayTds(ticketData);
  const promoDiscount = Number(discount ?? 0);
  const displayOtherCharges = Number(otherCharges);
  const displayTotal = getHotelTicketDisplayTotal(
    ticketData,
    fareTotals,
    pgTransactionFee,
  );
  const showPanColumn = shouldShowPanOnHotelTicket(ticketData);
  const showPassportColumn = shouldShowPassportOnHotelTicket(ticketData);
  const specialRequestText = getHotelSpecialRequestFromTicket(ticketData);
  const passengersByRoom = groupHotelTicketGuestsByRoom(ticketData).filter(
    (room) => (room?.paxInfoList ?? []).length > 0,
  );
  const collapseSamePanRoomForms = shouldCollapseHotelSamePanRoomForms(
    ticketData,
    { samePanForAll: samePanForAllRooms },
  );
  const isCancelledTicket = getEffectiveHotelBookingStatus(ticketData) === 6;
  const cancellationReasonLines = isCancelledTicket
    ? getHotelCancellationReasonLines(ticketData)
    : [];

  const renderPassengersDetailsCard = ({ samePanCheckbox = null } = {}) => (
    <Card className="card-tic-f2">
      <Row>
        <Col md={20} xs={24} className="city-tt-f4">
          <p>Passengers Details</p>
        </Col>
      </Row>
      {samePanCheckbox}
      {passengersByRoom.length > 0
        ? passengersByRoom.map((room, roomIndex) => (
            <div
              className="hotel-ticket-room-block"
              key={`ticket-passengers-room-${roomIndex}`}
            >
              <p className="room-title">
                <span>Room {roomIndex + 1}</span>
              </p>
              {(room?.paxInfoList ?? []).map((item, guestIndex) => (
                <Row
                  className="details-airlines-contact"
                  key={`ticket-passenger-${roomIndex}-${guestIndex}`}
                >
                  <Col md={6} xs={24} className="contact-person-d4">
                    <span>Name</span>
                    <p>{formatHotelGuestDisplayName(item)}</p>
                  </Col>
                  {item.Age !== undefined && item.Age !== null && (
                    <Col md={6} xs={24} className="contact-person-d4">
                      <span>Age</span>
                      <p>{item.Age}</p>
                    </Col>
                  )}
                  {showPassportColumn && isHotelAdultGuest(item) && (
                    <Col md={6} xs={24} className="contact-person-d4">
                      <span>Passport Number</span>
                      <p>
                        {item.PassportNo ||
                          item.passportNo ||
                          "—"}
                      </p>
                    </Col>
                  )}
                  {showPanColumn && isHotelAdultGuest(item) && (
                    <Col md={6} xs={24} className="contact-person-d4">
                      <span>Pan No.</span>
                      <p>{item.Pan || item.pan || "—"}</p>
                    </Col>
                  )}
                  <Col md={6} xs={24} className="contact-person-d4">
                    <span>Guest Type</span>
                    <p>{item.GuestType ?? item.guestType ?? "Adult"}</p>
                  </Col>
                </Row>
              ))}
            </div>
          ))
        : null}
    </Card>
  );

  const voucherPanSectionProps = {
    ticketData,
    samePanForAllRooms,
    verifiedPanRoomIndex,
    onSamePanChange: onSamePanForAllRoomsChange,
    onPanVerified: onTicketPanVerified,
    panFieldErrors,
    onPanFieldTouched,
  };

  return (
    <>
      <span className="tic-gredient-f"></span>
      <Layout className="cms-pages-width ourteam-bg hotel-ticket-page">
        <Row>
          <Col md={24} xs={24}>
            <Row>
              <Col md={24} xs={24}>
                <p className="tic-city-name-d2">{ticketData.HotelName}</p>
                <Card className="card-tic-f3">
                  <Row gutter={[16, 8]}>
                    <Col md={10} sm={24} xs={24}>
                      <div className="city-f3">
                        <p>{ticketData.HotelName}</p>
                        <span>{ticketData.CityName}</span>
                      </div>
                    </Col>
                    <Col className="d-flex justify-content-between" md={14} sm={24} xs={24}>
                    {hasHotelConfirmationNumber(ticketData) && (
                      <div className="city-f4">
                        <p className="word-break">
                          {getHotelConfirmationNumberForDisplay(ticketData)}
                        </p>
                        <span>Hotel Confirmation No</span>
                      </div>
                    )}
                    {(shouldShowHotelLastCancellationDate(ticketData) ||
                      shouldShowHotelLastVoucherDate(ticketData)) && (
                      <div className="city-f4">
                        {shouldShowHotelLastCancellationDate(ticketData) && (
                          <p className="word-break text-right">
                            <span>Last Cancellation Date :- </span>
                            <span className="text-danger">
                              {formatHotelTicketCancellationDateForDisplay(
                                ticketData,
                                "DD-MM-YYYY",
                              )}
                            </span>
                          </p>
                        )}
                        {shouldShowHotelLastVoucherDate(ticketData) && (
                          <p className="word-break text-right">
                            <span>Last Voucher Date :- </span>
                            <span className="text-danger">
                              {formatHotelTicketCancellationDateForDisplay(
                                ticketData,
                                "DD-MM-YYYY",
                              )}
                            </span>
                          </p>
                        )}
                      </div>
                    )}
                    </Col>
                  </Row>
                  <div className="border-hr"></div>
                  <Row className="row-top-f4">
                    <Col md={7} xs={24} className="city-f4">
                      <p>{ticketData.RefNumber}</p>
                      <span>Hotel Ref Number</span>
                    </Col>
                    <Col md={7} xs={24} className="city-f4">
                      <p className="cnfrom-f3">
                        {getStatus(getEffectiveHotelBookingStatus(ticketData))}
                      </p>
                      <span>Status</span>
                    </Col>
                    <Col md={6} xs={24} className="city-f4">
                      <p>{ticketData.NoOfRooms}</p>
                      <span>Room(s)</span>
                    </Col>
                    {/* <Col md={4} xs={24} className="city-f4">
                     {ticketData.pgStatus==0?<p>Due </p>:<p>Paid</p>} 
                      <span>Payment Status</span>
                    </Col> */}
                    {shouldShowHotelPaymentStatus(ticketData) && (
                    <Col md={4} xs={24} className="city-f4">
                      <p>{getHotelPaymentStatusText(ticketData)}</p>
                      <span>Payment Status</span>
                    </Col>
                    )}
                  </Row>
                </Card>

                <Card className="card-tic-f1">
                  <Row className="info-flight-t22">
                    <Col md={24} xs={24} className="city-tt-f4">
                      <p>Hotel Details</p>
                      <span>
                        Here are the details of your Hotel booked from{" "}
                        {formatDate(ticketData.CheckInDate, dateFormat)} to{" "}
                        {formatDate(ticketData.CheckOutDate, dateFormat)}
                      </span>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={8} xs={24} className="city-tt-f5">
                      <div className="logo-f3-text">
                        <div className="sg-logo-flight">
                          <p>
                            <strong>{ticketData.HotelName}</strong>
                          </p>

                          <p>{ticketData.CityName}</p>
                        </div>
                      </div>
                    </Col>
                    <Col md={16} xs={24} className="city-tt-f4">
                      <div className="city-from-d3">
                        <div className="city-f55">
                          <p>
                            {formatDate(ticketData.CheckInDate, dateFormat)}
                          </p>
                          <span>Check In</span>
                        </div>
                        <div className="city-f55">
                          <p className="dotted-line-flight"></p>
                          <span>{ticketData.CityName}</span>
                        </div>
                        <div className="city-f55">
                          <p>
                            {formatDate(ticketData.CheckOutDate, dateFormat)}
                          </p>
                          <span>Check Out</span>
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <Row gutter={[2, 8]}>
                    <Col md={10} xs={24} className="city-tt-f5">
                      <div className="sg-logo-flight">
                        <span>Hotel Address</span>
                        <p>
                          <strong> {ticketData.HotelAddress.address}</strong>
                        </p>
                      </div>
                    </Col>
                    <Col md={8} xs={24} className="city-tt-f5">
                      <div className="sg-logo-flight">
                        <span>Star Rating</span>
                        <p>
                          <strong> {ticketData.StarRating}</strong>
                        </p>
                      </div>
                    </Col>
                    <Col md={6} xs={24} className="city-tt-f5">
                      <div className="sg-logo-flight">
                        <span>No. of Nights</span>
                        <p>
                          <strong> {noOfNights}</strong>
                        </p>
                      </div>
                    </Col>
                  </Row>
                  <Row gutter={[2, 8]}>
                    <Col md={10} xs={0} className="city-tt-f5" />
                    <Col md={8} xs={24} className="city-tt-f5">
                      <div className="sg-logo-flight">
                        <span>No. of Guests</span>
                        <p>
                          <strong>{getHotelTicketGuestCount(ticketData)}</strong>
                        </p>
                      </div>
                    </Col>
                    <Col md={6} xs={24} className="city-tt-f5">
                      <div className="sg-logo-flight">
                        <span>No. of Rooms</span>
                        <p>
                          <strong>{getHotelTicketRoomCount(ticketData)}</strong>
                        </p>
                      </div>
                    </Col>
                  </Row>
                  <Row gutter={[2, 8]}>
                    {ticketData.Rooms.map((item, i) => {
                      const mealCategory = getRoomMealCategoryLabel(item);
                      return (
                      <Col
                        key={i + "room"}
                        md={16}
                        xs={24}
                        className="city-tt-f5"
                      >
                        <div className="sg-logo-flight">
                          <span>Room Name</span>
                          <p>
                            <strong> {formatRoomComboTitle(item.roomName)}</strong>
                          </p>
                          {mealCategory ? (
                            <p
                              className="mb-0"
                              style={{
                                color: "#5c6b8a",
                                fontWeight: 500,
                                fontSize: 14,
                                marginTop: 2,
                              }}
                            >
                              {mealCategory}
                            </p>
                          ) : null}
                        </div>
                      </Col>
                    );
                    })}
                  </Row>
                </Card>

                <Card className="card-tic-f2">
                  <Row>
                    <Col md={20} xs={24} className="city-tt-f4">
                      <p>Contact Details</p>
                    </Col>
                  </Row>

                  <Row className="details-airlines-contact">
                    <Col md={8} sm={12} xs={24} className="contact-person-d4">
                      <span>Name</span>
                      <p>
                        {" "}
                        {ticketData.guests.length > 0
                          ? formatHotelGuestDisplayName(ticketData.guests[0])
                          : null}
                      </p>
                    </Col>
                    <Col md={8} sm={12} xs={24} className="contact-person-d4">
                      <span>Mobile Number</span>
                      <p>{ticketData.phoneNo}</p>
                    </Col>

                    <Col md={8} sm={12} xs={24} className="contact-person-d4">
                      <span>Email</span>

                      <p>{ticketData.email}</p>
                    </Col>
                  </Row>
                </Card>

                {specialRequestText ? (
                  <Card className="card-tic-f2 hotel-ticket-special-request">
                    <Row>
                      <Col md={20} xs={24} className="city-tt-f4">
                        <p>Special Request</p>
                      </Col>
                    </Row>
                    <HotelSpecialRequest
                      value={specialRequestText}
                      readOnly
                      embedded
                    />
                  </Card>
                ) : null}

                {showVoucherMultiRoomPan && !collapseSamePanRoomForms ? (
                  <HotelTicketVoucherPanSection
                    {...voucherPanSectionProps}
                    hideRoomGuestForms={false}
                  />
                ) : null}
                {renderPassengersDetailsCard({
                  samePanCheckbox:
                    showVoucherMultiRoomPan && collapseSamePanRoomForms ? (
                      <HotelTicketVoucherPanSection
                        {...voucherPanSectionProps}
                        hideRoomGuestForms
                      />
                    ) : null,
                })}

                {ticketData?.insuranceRequired === 1 &&
                ticketData.insuranceData &&
                ticketData?.insuranceData?.serviceType === 2 ? (
                  <Card className="insurance-section card-tic-f2">
                    <h5 style={{ fontSize: "16px" }}>
                      THE SMART INSURANCE COVER
                    </h5>
                    <p>EASY CLAIM PROCESS | NO QUESTIONS ASKED</p>

                    <Row gutter={[16, 16]} align="stretch" className="mt-4">
                      <Col md={8} sm={24} xs={24}>
                        <div className="insurance-box">
                          {ticketData?.insuranceData.description_box_1}
                        </div>
                      </Col>
                      <Col md={8} sm={24} xs={24}>
                        <div className="insurance-box">
                          {ticketData?.insuranceData.description_box_2}
                        </div>
                      </Col>
                      <Col md={8} sm={24} xs={24}>
                        <div className="insurance-box">
                          {ticketData?.insuranceData.description_box_3}
                        </div>
                      </Col>
                    </Row>

                    <div className="insurance-coverage">
                      <SafetyCertificateOutlined />
                      <span>
                        Insurance Coverage Amount :{" "}
                        {ticketData?.insuranceData.insuranceCoverage}
                      </span>
                    </div>
                  </Card>
                ) : null}

                <Card className="card-tic-f2">
                  <Row>
                    <Col md={20} xs={24} className="city-tt-f4">
                      <p>Fare Details</p>
                    </Col>
                  </Row>

                  <Row className="details-airlines-contact">
                    <Col md={18} xs={8} sm={12} className="contact-person-d4">
                      <p>{isAgentB2B ? "Room Price" : "Base Fare"}</p>
                    </Col>
                    <Col md={6} xs={12} className="contact-person-d4">
                      <p>
                        {activeCurrency}{" "}
                        {currencyValue(
                          isAgentB2B
                            ? agentFareDisplay?.roomPrice
                            : baseAmount,
                        )}
                      </p>
                    </Col>
                  </Row>
                  <Row className="details-airlines-contact">
                    <Col md={18} xs={12} className="contact-person-d4">
                      <p>{isAgentB2B ? "Taxes and Fees" : "Taxes"}</p>
                    </Col>
                    <Col md={6} xs={12} className="contact-person-d4">
                      <p>
                        {activeCurrency}{" "}
                        {currencyValue(
                          isAgentB2B ? agentFareDisplay?.taxes : taxAmount,
                        )}
                      </p>
                    </Col>
                  </Row>
                  {!isAgentB2B ? (
                    <Row className="details-airlines-contact">
                      <Col md={18} xs={12} className="contact-person-d4">
                        <p>Other Charges</p>
                      </Col>
                      <Col md={6} xs={12} className="contact-person-d4">
                        <p>
                          {activeCurrency}{" "}
                          {currencyValue(displayOtherCharges)}
                        </p>
                      </Col>
                    </Row>
                  ) : null}
                  {Math.ceil(Math.abs(Number(convienenceFee) || 0)) >= 1 ? (
                  <Row className="details-airlines-contact">
                    <Col md={18} xs={12} className="contact-person-d4">
                      <p>Convenience Fee</p>
                    </Col>
                    <Col md={6} xs={12} className="contact-person-d4">
                      <p>{activeCurrency} {currencyValue(convienenceFee)}</p>
                    </Col>
                  </Row>
                  ) : null}
                  {Number(pgTransactionFee) > 0 ? (
                    <Row className="details-airlines-contact">
                      <Col md={18} xs={12} className="contact-person-d4">
                        <p>Transaction Fee</p>
                      </Col>
                      <Col md={6} xs={12} className="contact-person-d4">
                        <p>
                          {activeCurrency}{" "}
                          {currencyValue(pgTransactionFee)}
                        </p>
                      </Col>
                    </Row>
                  ) : null}
                  {ticketData?.insuranceRequired === 1 &&
                  ticketData.insuranceData ? (
                    <Row className="details-airlines-contact">
                      <Col md={18} xs={12} className="contact-person-d4">
                        <p>Insurance Amount</p>
                      </Col>
                      <Col md={6} xs={12} className="contact-person-d4">
                        <p>{activeCurrency} {currencyValue(insuranceTotal)}</p>
                      </Col>
                    </Row>
                  ) : null}
                  {isAgentB2B && commissionDiscount > 0 ? (
                    <Row className="details-airlines-contact">
                      <Col md={18} xs={12} className="contact-person-d4">
                        <p>- Commission Earned</p>
                      </Col>
                      <Col md={6} xs={12} className="contact-person-d4">
                        <p>
                          {activeCurrency}{" "}
                          {currencyValue(commissionDiscount)}
                        </p>
                      </Col>
                    </Row>
                  ) : null}
                  {isAgentB2B && displayTds > 0 ? (
                    <Row className="details-airlines-contact">
                      <Col md={18} xs={12} className="contact-person-d4">
                        <p>+ TDS</p>
                      </Col>
                      <Col md={6} xs={12} className="contact-person-d4">
                        <p>
                          {activeCurrency} {currencyValue(displayTds)}
                        </p>
                      </Col>
                    </Row>
                  ) : null}
                  {!isAgentB2B ? (
                    <Row className="details-airlines-contact">
                      <Col md={18} xs={12} className="contact-person-d4">
                        <p>Discount</p>
                      </Col>
                      <Col md={6} xs={12} className="contact-person-d4">
                        <p>
                          {activeCurrency} {currencyValue(promoDiscount)}
                        </p>
                      </Col>
                    </Row>
                  ) : promoDiscount > 0 ? (
                    <Row className="details-airlines-contact">
                      <Col md={18} xs={12} className="contact-person-d4">
                        <p>Discount</p>
                      </Col>
                      <Col md={6} xs={12} className="contact-person-d4">
                        <p>
                          {activeCurrency} {currencyValue(promoDiscount)}
                        </p>
                      </Col>
                    </Row>
                  ) : null}
                  <Row className="details-airlines-contact">
                    <Col md={18} xs={12} className="contact-person-d4">
                      <p>{isAgentB2B ? "Final Payment" : "Total"}</p>
                    </Col>
                    <Col md={6} xs={12} className="contact-person-d4">
                      <p>{activeCurrency} {currencyValue(displayTotal)}</p>
                    </Col>
                  </Row>
                </Card>
                {isCancelledTicket && cancellationReasonLines.length > 0 ? (
                  <Card className="card-tic-f2">
                    <Row>
                      <Col md={20} xs={24} className="city-tt-f4">
                        <p>Cancellation Reason:</p>
                      </Col>
                    </Row>
                    <Row className="details-airlines-contact booking-flight-details-info pt-0">
                      <Col md={24} xs={24} className="contact-person-d4">
                        <div className="insurance-box">
                          {cancellationReasonLines.map((line, index) => (
                            <p
                              key={`hotel-cancel-reason-${index}`}
                              className={
                                index === cancellationReasonLines.length - 1
                                  ? "mb-0"
                                  : "mb-1"
                              }
                            >
                              {line}
                            </p>
                          ))}
                        </div>
                      </Col>
                    </Row>
                  </Card>
                ) : null}
                <Card className="card-tic-f2">
                  <Row>
                    <Col md={20} xs={24} className="city-tt-f4">
                      <p>Note </p>
                    </Col>
                  </Row>

                  <Row className="details-airlines-contact booking-flight-details-info pt-0">
                    <Col md={24} xs={24} className="contact-person-d4">
                      {cmsFareRules?.cancelPolicyDescription ? (
                        <div className="termCon_wrapper">
                          <div className="description">
                            <p className="title">Cancel Policy</p>
                            {parse(
                              cmsFareRules?.cancelPolicyDescription
                            )}
                            {cmsFareRules?.covidRulesDescription ? (
                              <>
                                <p className="title">Covid Policy</p>

                                {parse(
                                  cmsFareRules?.covidRulesDescription
                                )}
                              </>
                            ) : null}

                            {cmsFareRules?.otherRulesDescription ? (
                              <>
                                <p className="title">Other Policy</p>

                                {parse(
                                  cmsFareRules?.otherRulesDescription
                                )}
                              </>
                            ) : null}
                          </div>
                        </div>
                      ) : (
                        <div className="termCon_wrapper">
                          <p>
                            * Early check in and late check out is subject to
                            availability.
                          </p>

                          <p>
                            * The passenger has to furnish a photo identity and
                            address proof at the time of verification failing to
                            which all passenger on that voucher shall not be
                            entertained with any refunds or restitution.
                          </p>
                          <p>
                            * Standard Check in time is 1400 hrs / check out
                            time 1200 hrs unless specified.
                          </p>

                          <p>
                            * The refund for early check out or unutilized night
                            or service is subject to discretion of the hotel &
                            the supplier, we strongly recommend to get a letter
                            from the hotel favoring no charges for early check
                            out.
                          </p>

                          <p>
                            * All additional charges other than the room charges
                            and inclusions as mentioned in the booking voucher
                            are to be borne and paid separately during
                            check-out. Please make sure that you are aware of
                            all such charges that may comes as extras. Some of
                            them can be WiFi costs, Mini Bar, Laundry Expenses,
                            Telephone calls, Room Service, Snacks etc.
                          </p>

                          <p>
                            * Voucher covers only those taxes which are included
                            in the booking price (if included), all various
                            other applicable taxes needs to be settled directly
                            by guest .
                          </p>
                          <p>
                            * We are not liable for any loss or damage caused
                            during your stay in the hotel or while availing any
                            service.
                          </p>

                          <p>
                            * We have no relevance with the room service or food
                            menu or any other service as these are all as per
                            the hotel standards.
                          </p>

                          <p>
                            * The hotel reserves the right of admission.
                            Accommodation can be denied to guests posing as a
                            'couple' if suitable proof of identification is not
                            presented at the time of check in. we will not be
                            responsible for any check in denied by the hotel due
                            to aforesaid reason.
                          </p>
                        </div>
                      )}
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </Col>

          <Col md={6} xs={24}></Col>
        </Row>
      </Layout>
    </>
  );
};

export default TicketHotel;
